import { ChevronDown, FolderKanban, Info, Plus, Tag as TagIcon, UserPlus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../api/errors';
import { useUserMap } from '../../api/org';
import { canEditProject, useCreateTag, useProject, useProjects, useTags } from '../../api/projects';
import { useAddTaskToProject, useCreateTask } from '../../api/tasks';
import type { CreateTaskInput, Task } from '../../api/types';
import { useLastProject } from '../../app/prefs';
import { useSession } from '../../app/session';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { type Option, SearchSelect, SelectButton } from '../../components/ui/Combobox';
import { DateField } from '../../components/ui/DatePicker';
import { Dialog } from '../../components/ui/Dialog';
import { Checkbox, Field, FormAlert, Input, Select, Switch, Textarea } from '../../components/ui/Field';
import { useToast } from '../../components/ui/Toast';
import { cn } from '../../lib/cn';
import { formatDate } from '../../lib/dates';
import { MOD } from '../../lib/platform';
import { describeSensitive, detectSensitive, maskSensitive } from '../../lib/sensitive';
import { useUserOptions } from './TaskControls';
import { defaultDue, lateAssignSuggestion, PRIORITY_LABELS, SUBTYPE_LABELS } from './taskUtils';

export type CreateDefaults = {
  projectId?: string;
  sectionId?: string;
  parentId?: string;
  parentKey?: string;
  dueOn?: string;
  assigneeId?: string;
  title?: string;
};

type Props = { open: boolean; defaults: CreateDefaults; onClose: () => void; onCreated: (task: Task) => void };

/** Швидке створення задачі (Q). Форма веде до правильної дії: один виконавець, строк, проєкт. */
export function CreateTaskDialog({ open, defaults, onClose, onCreated }: Props) {
  const { userId, policy } = useSession();
  const users = useUserMap();
  const userOptions = useUserOptions();
  const projects = useProjects();
  const [lastProject, setLastProject] = useLastProject();
  const create = useCreateTask();
  const addToProject = useAddTaskToProject();
  const tags = useTags();
  const createTag = useCreateTag();
  const toast = useToast();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assigneeId, setAssigneeId] = useState<string | null>(userId);
  const [dueOn, setDueOn] = useState<string | null>(null);
  const [dueTouched, setDueTouched] = useState(false);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [sectionId, setSectionId] = useState<string | null>(null);
  const [followerIds, setFollowerIds] = useState<string[]>([]);
  const [collaboratorIds, setCollaboratorIds] = useState<string[]>([]);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [tagQuery, setTagQuery] = useState('');
  /** «Ще проєкти»: задача одразу стоїть у кількох проєктах (у кожному — на першому етапі). */
  const [extraProjectIds, setExtraProjectIds] = useState<string[]>([]);
  const [subtype, setSubtype] = useState<CreateTaskInput['subtype']>('task');
  const [priority, setPriority] = useState<NonNullable<CreateTaskInput['priority']>>('normal');
  const [urgent, setUrgent] = useState(false);
  const [deadlineOn, setDeadlineOn] = useState<string | null>(null);
  const [startOn, setStartOn] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [allowSensitive, setAllowSensitive] = useState(false);
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const [picker, setPicker] = useState<{ kind: 'assignee' | 'project' | 'section' | 'followers' | 'collaborators' | 'tags' | 'extraProjects'; el: HTMLElement } | null>(null);

  // Скидання форми під час відкриття.
  useEffect(() => {
    if (!open) return;
    const a = defaults.assigneeId ?? userId;
    setTitle(defaults.title ?? '');
    setDescription('');
    setAssigneeId(a);
    setDueOn(defaults.dueOn ?? defaultDue(policy, a, userId));
    setDueTouched(!!defaults.dueOn);
    setProjectId(defaults.parentId ? (defaults.projectId ?? null) : (defaults.projectId ?? lastProject ?? null));
    setSectionId(defaults.sectionId ?? null);
    setFollowerIds([]);
    setCollaboratorIds([]);
    setTagIds([]);
    setTagQuery('');
    setExtraProjectIds([]);
    setSubtype('task');
    setPriority('normal');
    setUrgent(false);
    setDeadlineOn(null);
    setStartOn(null);
    setMore(false);
    setAllowSensitive(false);
    setLocalErrors({});
    create.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const editableProjects = useMemo(() => (projects.data ?? []).filter((p) => !p.archivedAt && canEditProject(p.myRole)), [projects.data]);
  const project = useProject(projectId ?? undefined);
  const sections = project.data?.sections ?? [];

  const projectOptions: Option[] = useMemo(
    () =>
      editableProjects.map((p) => ({
        value: p.id,
        label: p.name,
        description: p.processCode ? `Процес ${p.processCode}` : undefined,
        icon: <FolderKanban className="size-4 text-fg-3" aria-hidden />,
        group: p.type === 'process' ? 'Процесні потоки' : 'Проєкти',
      })),
    [editableProjects],
  );
  const sectionOptions: Option[] = sections.map((s) => ({
    value: s.id,
    label: s.name,
    description: [s.slaHours ? `норматив ${s.slaHours}\u00a0год` : null, s.wipLimit ? `ліміт ${s.wipLimit}` : null].filter(Boolean).join(', ') || undefined,
  }));

  const serverError = create.error instanceof ApiError ? create.error : null;
  const fieldErr = (f: string) => localErrors[f] ?? serverError?.fieldError(f);

  const assignee = assigneeId ? users.get(assigneeId) : undefined;
  const late = lateAssignSuggestion(policy, assigneeId, userId, dueOn);
  const manager = assignee?.managerId ? users.get(assignee.managerId) : undefined;
  const needsManager = policy.requireProject && !projectId && !defaults.parentId;
  const managerIncluded = !!manager && (followerIds.includes(manager.id) || collaboratorIds.includes(manager.id) || manager.id === userId);
  const sensitive = detectSensitive(`${title}\n${description}`);

  const changeAssignee = (id: string) => {
    setAssigneeId(id);
    if (!dueTouched) setDueOn(defaultDue(policy, id, userId));
  };

  // Правила фірми перевіряє сервер (політика може змінюватися власником); форма лише підказує,
  // а відмову сервера (422 rule_violation) показує дослівно біля полів. Локально — тільки порожня назва.
  const validate = () => {
    const e: Record<string, string> = {};
    if (!title.trim()) e.title = 'Назва обов’язкова';
    setLocalErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = (force = false) => {
    if (!validate()) return;
    const body: CreateTaskInput = {
      title: title.trim(),
      description: description.trim() || undefined,
      assigneeId,
      projectId: projectId ?? undefined,
      sectionId: projectId && sectionId ? sectionId : undefined,
      parentId: defaults.parentId,
      subtype,
      priority,
      urgent,
      dueOn,
      deadlineOn: deadlineOn ?? undefined,
      startOn: startOn ?? undefined,
      followerIds: followerIds.length ? followerIds : undefined,
      // Учасники (працюють у задачі, не відповідальні) — поле ще не описане в типах SDK.
      ...(collaboratorIds.length ? ({ collaboratorIds } as Record<string, unknown>) : {}),
      tagIds: tagIds.length ? tagIds : undefined,
      allowSensitive: force || allowSensitive || undefined,
    };
    const extra = extraProjectIds.filter((id) => id !== projectId);
    create.mutate(body, {
      onSuccess: async (task) => {
        if (projectId && !defaults.parentId) setLastProject(projectId);
        onClose();
        // Задачу створено; до решти проєктів додаємо по одному — відмову (права, правило проєкту) показуємо окремо.
        const failed: string[] = [];
        for (const id of extra) {
          try {
            await addToProject.mutateAsync({ id: task.id, projectId: id });
          } catch (e) {
            const name = editableProjects.find((p) => p.id === id)?.name ?? 'проєкт';
            failed.push(e instanceof ApiError ? `«${name}»: ${e.detail}` : `«${name}»`);
          }
        }
        if (failed.length) toast.error(`Задачу ${task.key} створено, але не додано до всіх проєктів`, failed.join(' '));
        onCreated(task);
      },
    });
  };

  const tagById = new Map((tags.data ?? []).map((t) => [t.id, t]));
  const tagExists = (tags.data ?? []).some((t) => t.name.toLocaleLowerCase('uk') === tagQuery.trim().toLocaleLowerCase('uk'));
  const extraCount = tagIds.length + extraProjectIds.length;

  const knownFields = ['title', 'assigneeId', 'dueOn', 'projectId', 'sectionId', 'description', 'deadlineOn', 'followerIds', 'collaboratorIds', 'tagIds'];
  const otherErrors = serverError?.errors.filter((e) => !knownFields.includes(e.field)).map((e) => e.message) ?? [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={defaults.parentId ? 'Нова підзадача' : 'Нова задача'}
      description={defaults.parentKey ? `Підзадача до ${defaults.parentKey}` : 'Кожне доручення стає задачею з виконавцем, строком і проєктом.'}
      size="md"
      dismissible={!title && !description}
      onSubmit={() => submit(false)}
      footer={
        <>
          <span className="mr-auto hidden text-caption text-fg-3 sm:inline">
            <kbd className="key">{MOD}</kbd> + <kbd className="key">Enter</kbd> — створити
          </span>
          <Button variant="ghost" onClick={onClose}>
            Скасувати
          </Button>
          <Button type="submit" variant="primary" loading={create.isPending}>
            Створити задачу
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {serverError && (serverError.isRuleViolation || serverError.status >= 400) && !serverError.isSensitive && (
          <FormAlert detail={serverError.detail} items={otherErrors} />
        )}

        <Field label="Назва" required error={fieldErr('title')}>
          <Input data-autofocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Що потрібно зробити" maxLength={500} autoComplete="off" />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Виконавець" required={policy.requireAssignee} requiredNote="один" error={fieldErr('assigneeId')}>
            <SelectButton
              onClick={(e) => setPicker({ kind: 'assignee', el: e.currentTarget })}
              icon={assignee ? <Avatar name={assignee.name} src={assignee.avatarUrl} size={20} /> : <Avatar empty size={20} />}
              display={assignee ? (assignee.id === userId ? `${assignee.name} (я)` : assignee.name) : undefined}
              placeholder="Оберіть виконавця"
            />
          </Field>
          <Field label="Строк" required={policy.requireDueDate && subtype !== 'milestone'} error={fieldErr('dueOn')}>
            <DateField
              id="create-task-due"
              value={dueOn}
              onChange={(v) => {
                setDueOn(v);
                setDueTouched(true);
              }}
              allowClear={!policy.requireDueDate}
            />
          </Field>
        </div>

        {late && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-sm border border-info-line bg-info-soft px-3 py-2 text-meta text-info-fg">
            <Info className="size-4 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1">
              Після {policy.lateAssignHour}:00 доручення іншому працівнику ставлять не раніше ніж на наступний робочий день.
            </span>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setDueOn(late);
                setDueTouched(true);
                // Кнопка зникне разом із підказкою — фокус переходить на поле строку.
                requestAnimationFrame(() => document.getElementById('create-task-due')?.focus());
              }}
            >
              Поставити на {formatDate(late)}
            </Button>
          </div>
        )}

        {!defaults.parentId && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Проєкт"
              required={policy.requireProject && !managerIncluded}
              error={fieldErr('projectId')}
              hint={needsManager ? 'Без проєкту задача допустима лише за участі керівника виконавця.' : undefined}
            >
              <SelectButton
                onClick={(e) => setPicker({ kind: 'project', el: e.currentTarget })}
                icon={<FolderKanban className="size-4 shrink-0 text-fg-3" aria-hidden />}
                display={projectId ? (editableProjects.find((p) => p.id === projectId)?.name ?? project.data?.name) : undefined}
                placeholder="Оберіть проєкт"
              />
            </Field>
            <Field label="Етап" error={fieldErr('sectionId')} hint={projectId && !sectionId ? 'Типово — перший етап проєкту.' : undefined}>
              <SelectButton
                disabled={!projectId}
                onClick={(e) => setPicker({ kind: 'section', el: e.currentTarget })}
                display={sectionId ? sections.find((s) => s.id === sectionId)?.name : undefined}
                placeholder={projectId ? (sections[0]?.name ?? 'Перший етап') : 'Спершу оберіть проєкт'}
              />
            </Field>
          </div>
        )}

        {needsManager && manager && !managerIncluded && (
          <div className="flex flex-wrap items-center gap-2 text-meta text-fg-2">
            <span>Керівник виконавця: {manager.name}.</span>
            <Button size="sm" variant="secondary" icon={<UserPlus className="size-3.5" aria-hidden />} onClick={() => setFollowerIds((f) => [...f, manager.id])}>
              Додати спостерігачем
            </Button>
          </div>
        )}

        <Field label="Опис" error={fieldErr('description')} hint="Очікуваний результат, посилання на документи, контекст.">
          <Textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        {sensitive.length > 0 && (
          <FormAlert
            tone="warning"
            title="Схоже, у тексті є чутливі дані"
            detail={`Виявлено: ${describeSensitive(sensitive)}. Паролі й реквізити карток не зберігають у задачах.`}
          >
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setTitle(maskSensitive(title));
                  setDescription(maskSensitive(description));
                }}
              >
                Замаскувати
              </Button>
              <Checkbox checked={allowSensitive} onChange={setAllowSensitive} label="Зберегти як є — я розумію ризик" />
            </div>
          </FormAlert>
        )}
        {serverError?.isSensitive && (
          <FormAlert tone="warning" title="Сервер виявив чутливі дані" detail={serverError.detail}>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setTitle(maskSensitive(title));
                  setDescription(maskSensitive(description));
                  create.reset();
                }}
              >
                Замаскувати
              </Button>
              <Button size="sm" variant="ghost" onClick={() => submit(true)}>
                Зберегти як є
              </Button>
            </div>
          </FormAlert>
        )}

        <Field label="Учасники" error={fieldErr('collaboratorIds')} hint="Працюють у задачі (поля, підзадачі, коментарі, облік часу), але не відповідальні — напр. КМ у «Відпрацюванні».">
          <div className="flex flex-wrap items-center gap-1.5">
            {collaboratorIds.map((id) => {
              const u = users.get(id);
              return (
                <span key={id} className="inline-flex h-7 items-center gap-1.5 rounded-sm border border-line bg-surface-2 pr-1 pl-1.5 text-meta">
                  <Avatar name={u?.name} size={16} />
                  {u?.name ?? 'Працівник'}
                  <button
                    type="button"
                    aria-label={`Прибрати ${u?.name ?? 'учасника'}`}
                    onClick={() => setCollaboratorIds((f) => f.filter((x) => x !== id))}
                    className="inline-flex size-5 items-center justify-center rounded-xs text-fg-3 hover:bg-surface-3 hover:text-fg"
                  >
                    <X className="size-3" aria-hidden />
                  </button>
                </span>
              );
            })}
            <Button size="sm" variant="ghost" icon={<UserPlus className="size-3.5" aria-hidden />} onClick={(e) => setPicker({ kind: 'collaborators', el: e.currentTarget })}>
              Додати
            </Button>
          </div>
        </Field>

        <Field label="Спостерігачі" error={fieldErr('followerIds')} hint="Бачать задачу, коментують і отримують сповіщення. Виконавець і автор додаються автоматично.">
          <div className="flex flex-wrap items-center gap-1.5">
            {followerIds.map((id) => {
              const u = users.get(id);
              return (
                <span key={id} className="inline-flex h-7 items-center gap-1.5 rounded-sm border border-line bg-surface-2 pr-1 pl-1.5 text-meta">
                  <Avatar name={u?.name} size={16} />
                  {u?.name ?? 'Працівник'}
                  <button
                    type="button"
                    aria-label={`Прибрати ${u?.name ?? 'учасника'}`}
                    onClick={() => setFollowerIds((f) => f.filter((x) => x !== id))}
                    className="inline-flex size-5 items-center justify-center rounded-xs text-fg-3 hover:bg-surface-3 hover:text-fg"
                  >
                    <X className="size-3" aria-hidden />
                  </button>
                </span>
              );
            })}
            <Button size="sm" variant="ghost" icon={<UserPlus className="size-3.5" aria-hidden />} onClick={(e) => setPicker({ kind: 'followers', el: e.currentTarget })}>
              Додати
            </Button>
          </div>
        </Field>

        <button
          type="button"
          aria-expanded={more}
          onClick={() => setMore((m) => !m)}
          className="-mx-1 inline-flex w-fit items-center gap-1.5 rounded-sm px-1 py-0.5 text-meta font-medium text-fg-2 hover:text-fg"
        >
          <ChevronDown className={cn('ers-turn size-4', more && 'rotate-180')} aria-hidden />
          Додаткові параметри
          {!more && extraCount > 0 && <span className="font-normal text-fg-3">· теги й проєкти: {extraCount}</span>}
        </button>
        {more && (
          <div className="grid gap-4 rounded-md border border-line bg-canvas p-3 sm:grid-cols-2">
            <Field label="Тип">
              <Select value={subtype} onChange={(e) => setSubtype(e.target.value as CreateTaskInput['subtype'])}>
                {(Object.keys(SUBTYPE_LABELS) as NonNullable<CreateTaskInput['subtype']>[]).map((k) => (
                  <option key={k} value={k}>
                    {SUBTYPE_LABELS[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Пріоритет">
              <Select value={priority} onChange={(e) => setPriority(e.target.value as typeof priority)}>
                {(Object.keys(PRIORITY_LABELS) as (typeof priority)[]).map((k) => (
                  <option key={k} value={k}>
                    {PRIORITY_LABELS[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Крайній строк" hint="Для клієнта чи органу; дата виконання не може бути пізнішою." error={fieldErr('deadlineOn')}>
              <DateField value={deadlineOn} onChange={setDeadlineOn} allowClear placeholder="Не вказано" />
            </Field>
            <Field label="Дата початку">
              <DateField value={startOn} onChange={setStartOn} allowClear placeholder="Не вказано" />
            </Field>
            <Field className="sm:col-span-2" label="Теги" error={fieldErr('tagIds')}>
              <div className="flex flex-wrap items-center gap-1.5">
                {tagIds.map((id) => (
                  <span key={id} className="inline-flex h-7 items-center gap-1.5 rounded-sm border border-line bg-surface pr-1 pl-1.5 text-meta">
                    <TagIcon className="size-3 text-fg-3" aria-hidden />
                    {tagById.get(id)?.name ?? 'тег'}
                    <button
                      type="button"
                      aria-label={`Прибрати тег ${tagById.get(id)?.name ?? ''}`}
                      onClick={() => setTagIds((t) => t.filter((x) => x !== id))}
                      className="inline-flex size-5 items-center justify-center rounded-xs text-fg-3 hover:bg-surface-3 hover:text-fg"
                    >
                      <X className="size-3" aria-hidden />
                    </button>
                  </span>
                ))}
                <Button size="sm" variant="ghost" icon={<Plus className="size-3.5" aria-hidden />} onClick={(e) => setPicker({ kind: 'tags', el: e.currentTarget })}>
                  Додати тег
                </Button>
              </div>
            </Field>
            {!defaults.parentId && (
              <Field className="sm:col-span-2" label="Ще проєкти" hint="Задача стоятиме одразу в кількох проєктах; у кожному — на першому етапі. Основний проєкт — вище.">
                <div className="flex flex-wrap items-center gap-1.5">
                  {extraProjectIds.map((id) => (
                    <span key={id} className="inline-flex h-7 max-w-full items-center gap-1.5 rounded-sm border border-line bg-surface pr-1 pl-1.5 text-meta">
                      <FolderKanban className="size-3.5 shrink-0 text-fg-3" aria-hidden />
                      <span className="truncate">{editableProjects.find((p) => p.id === id)?.name ?? 'Проєкт'}</span>
                      <button
                        type="button"
                        aria-label={`Прибрати проєкт ${editableProjects.find((p) => p.id === id)?.name ?? ''}`}
                        onClick={() => setExtraProjectIds((t) => t.filter((x) => x !== id))}
                        className="inline-flex size-5 shrink-0 items-center justify-center rounded-xs text-fg-3 hover:bg-surface-3 hover:text-fg"
                      >
                        <X className="size-3" aria-hidden />
                      </button>
                    </span>
                  ))}
                  <Button size="sm" variant="ghost" disabled={!projectId} icon={<Plus className="size-3.5" aria-hidden />} onClick={(e) => setPicker({ kind: 'extraProjects', el: e.currentTarget })}>
                    Додати проєкт
                  </Button>
                  {!projectId && <span className="text-caption text-fg-3">Спершу оберіть основний проєкт.</span>}
                </div>
              </Field>
            )}
            <Switch className="sm:col-span-2" checked={urgent} onChange={setUrgent} label="Терміново" description="Позначка «ТЕРМІНОВО» й негайне сповіщення виконавцю." />
            {subtype === 'approval' && (
              <p className="text-meta text-fg-2 sm:col-span-2">Задача-погодження завершується рішенням виконавця: погодити, на доопрацювання або відхилити.</p>
            )}
          </div>
        )}
      </div>

      <SearchSelect
        open={picker?.kind === 'assignee'}
        onClose={() => setPicker(null)}
        anchor={picker?.el ?? null}
        options={userOptions}
        value={assigneeId}
        onSelect={changeAssignee}
        label="Виконавець"
        placeholder="Пошук працівника"
      />
      <SearchSelect
        open={picker?.kind === 'followers'}
        onClose={() => setPicker(null)}
        anchor={picker?.el ?? null}
        options={userOptions.filter((o) => o.value !== assigneeId)}
        value={followerIds}
        multiple
        onSelect={(id) => {
          setCollaboratorIds((c) => c.filter((x) => x !== id));
          setFollowerIds((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));
        }}
        label="Спостерігачі"
        placeholder="Пошук працівника"
      />
      <SearchSelect
        open={picker?.kind === 'collaborators'}
        onClose={() => setPicker(null)}
        anchor={picker?.el ?? null}
        options={userOptions.filter((o) => o.value !== assigneeId)}
        value={collaboratorIds}
        multiple
        onSelect={(id) => {
          setFollowerIds((f) => f.filter((x) => x !== id));
          setCollaboratorIds((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
        }}
        label="Учасники"
        placeholder="Пошук працівника"
      />
      <SearchSelect
        open={picker?.kind === 'tags'}
        onClose={() => {
          setPicker(null);
          setTagQuery('');
        }}
        anchor={picker?.el ?? null}
        options={(tags.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
        value={tagIds}
        multiple
        label="Теги"
        placeholder="Пошук або новий тег"
        emptyText={tagQuery.trim() ? 'Такого тегу ще немає' : 'Тегів ще немає — введіть назву, щоб створити'}
        onQueryChange={setTagQuery}
        onSelect={(id) => setTagIds((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]))}
        footer={
          tagQuery.trim() && !tagExists ? (
            <button
              type="button"
              onClick={() =>
                createTag.mutate(
                  { name: tagQuery.trim() },
                  { onSuccess: (t) => setTagIds((ids) => [...ids, t.id]), onError: (e) => toast.error('Тег не створено', e instanceof ApiError ? e.detail : undefined) },
                )
              }
              className="flex w-full items-center gap-2 rounded-sm px-2.5 py-1.5 text-left text-meta hover:bg-surface-2"
            >
              <Plus className="size-3.5" aria-hidden />
              Створити тег «{tagQuery.trim()}»
            </button>
          ) : undefined
        }
      />
      <SearchSelect
        open={picker?.kind === 'extraProjects'}
        onClose={() => setPicker(null)}
        anchor={picker?.el ?? null}
        options={projectOptions.filter((o) => o.value !== projectId)}
        value={extraProjectIds}
        multiple
        onSelect={(id) => setExtraProjectIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))}
        label="Ще проєкти"
        placeholder="Пошук проєкту"
        emptyText="Немає інших проєктів, де ви можете створювати задачі"
      />
      <SearchSelect
        open={picker?.kind === 'project'}
        onClose={() => setPicker(null)}
        anchor={picker?.el ?? null}
        options={projectOptions}
        value={projectId}
        onSelect={(id) => {
          setProjectId(id);
          setSectionId(null);
          setExtraProjectIds((p) => p.filter((x) => x !== id));
        }}
        label="Проєкт"
        placeholder="Пошук проєкту"
        emptyText={projects.isLoading ? 'Завантаження…' : 'Немає проєктів, де ви можете створювати задачі'}
        footer={
          projectId ? (
            <button type="button" onClick={() => { setProjectId(null); setSectionId(null); setPicker(null); }} className="w-full rounded-sm px-2.5 py-1.5 text-left text-meta text-fg-2 hover:bg-surface-2">
              Без проєкту
            </button>
          ) : undefined
        }
      />
      <SearchSelect
        open={picker?.kind === 'section'}
        onClose={() => setPicker(null)}
        anchor={picker?.el ?? null}
        options={sectionOptions}
        value={sectionId}
        onSelect={setSectionId}
        label="Етап"
        placeholder="Пошук етапу"
        loading={project.isLoading}
      />
    </Dialog>
  );
}
